/* TAB 5 — Risk Tools: position sizer, risk-of-ruin calc, ET session timer, Golden Rules tracker, risk settings. */
import { useState, useEffect, useMemo, useCallback } from "react";
import { C, Chip, todayKey, inputStyle } from "./_shared";
import { useNow, fmtTime, computeMarketPhase } from "./_worldClock";
import { useAccountSize } from "./_accountSize";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function apiJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* noop */ }
    throw new Error(detail);
  }
  if (res.status === 204) return null;
  const txt = await res.text();
  return txt ? JSON.parse(txt) : null;
}

const GR_SECTIONS = [
  { title:"Risk management", color:C.red, rules:[
    "Never risk more than 1% of account on a single trade",
    "Set a daily max loss (2–3%) — if hit, stop trading for the day",
    "Stop after 3 consecutive losses — take a break, review, come back tomorrow",
    "Never add to a losing position — ever",
    "Always set your stop BEFORE you enter the trade",
  ]},
  { title:"Entry discipline", color:C.amber, rules:[
    "Only trade during Market Open (9:30–11am) and Power Hour (3–4pm)",
    "Minimum R:R of 2:1 required before any entry",
    "All 3+ indicator confirmations must align before entry",
    "Never chase — if you miss the entry, wait for the next setup",
    "Volume must confirm — no volume = no trade",
  ]},
  { title:"Exit discipline", color:C.green, rules:[
    "Let winners run — scale out at 50% target, trail the rest",
    "Cut losers fast — stop is a stop, not a suggestion",
    "All positions closed by 3:55pm ET — no overnight holds",
    "Never hold through earnings, Fed announcements, or CPI prints",
    "Take partial profits at first target to reduce anxiety",
  ]},
  { title:"Mental rules", color:C.indigo, rules:[
    "Trade the plan, not your emotions",
    "A losing day is fine — a blown account is not",
    "Keep a trade journal — review every Friday",
    "Don't watch P&L while in a trade — watch price action",
    "If you're angry, tired, or distracted — don't trade",
  ]},
  { title:"Pre-Market rules", color:"#0891b2", rules:[
    "Complete the morning checklist before placing any trade",
    "Know the key levels for every stock you plan to trade today",
    "Check earnings calendar — never trade a stock reporting today",
    "Confirm broad market direction before first trade",
    "Have your position sizes calculated before the open",
  ]},
  { title:"Post-Market rules", color:"#7c3aed", rules:[
    "Log every trade in the journal before closing the app",
    "Review what went right and wrong — one lesson per day minimum",
    "Calculate today's P&L and compare to your plan",
    "If you had a losing day, identify the root cause before tomorrow",
    "Never carry emotional baggage from today into tomorrow's session",
  ]},
];

const ruleId = (secIdx, ruleIdx) => `${secIdx}-${ruleIdx}`;
const GR_TOTAL = () => GR_SECTIONS.reduce((a,s)=>a+s.rules.length, 0);
const GR_FLAT = () => {
  const out = [];
  GR_SECTIONS.forEach((s, si) => s.rules.forEach((r, ri) => out.push({ id: ruleId(si,ri), text: r, color: s.color, sectionTitle: s.title })));
  return out;
};

// Deterministic daily focus — same 3 rules all day, rotates with date.
function todaysFocusRules() {
  const flat = GR_FLAT();
  const seedStr = todayKey();
  let seed = 0;
  for (let i=0; i<seedStr.length; i++) seed = (seed*31 + seedStr.charCodeAt(i)) >>> 0;
  const picks = new Set();
  let s = seed;
  while (picks.size < 3) {
    s = (1103515245 * s + 12345) >>> 0;
    picks.add(s % flat.length);
  }
  return [...picks].map(i => flat[i]);
}

function GoldenRulesPanel() {
  const [committed, setCommitted] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dt_rules_committed") || "{}"); } catch { return {}; }
  });
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dt_rules_notes") || "{}"); } catch { return {}; }
  });
  const [editingNote, setEditingNote] = useState(null);
  const [broken, setBroken] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("dt_rules_broken") || "{}");
      if (raw.date === todayKey()) return raw;
    } catch {}
    return { date: todayKey(), entries: [] };
  });

  useEffect(() => { localStorage.setItem("dt_rules_committed", JSON.stringify(committed)); }, [committed]);
  useEffect(() => { localStorage.setItem("dt_rules_notes",     JSON.stringify(notes));     }, [notes]);
  useEffect(() => { localStorage.setItem("dt_rules_broken",    JSON.stringify(broken));    }, [broken]);

  const focus = useMemo(todaysFocusRules, []);
  const focusIds = new Set(focus.map(f => f.id));
  const totalRules = GR_TOTAL();
  const committedCount = Object.values(committed).filter(Boolean).length;
  const allCommitted   = committedCount === totalRules;

  const toggleCommit = (id) => setCommitted(c => ({ ...c, [id]: !c[id] }));
  const recordBroken = (id, text) => setBroken(b => ({
    date: todayKey(),
    entries: [...b.entries, { id, text, ts: new Date().toISOString() }],
  }));
  const setNote = (id, text) => setNotes(n => ({ ...n, [id]: text }));

  const brokenCount = broken.entries.length;
  const brokenWarn  = brokenCount >= 3;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>

      <div style={{background:allCommitted?C.gb:"#fff",border:`2px solid ${allCommitted?C.green:"#e5e7eb"}`,borderRadius:12,padding:"16px 18px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <div style={{fontSize:32,fontWeight:900,color:allCommitted?C.green:C.indigo,lineHeight:1,letterSpacing:"-0.03em"}}>{committedCount}<span style={{fontSize:18,opacity:0.5}}>/{totalRules}</span></div>
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:11,color:C.gray,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:2}}>Rule Commitment</div>
          <div style={{fontSize:13,fontWeight:700,color:allCommitted?C.gt:"#111"}}>
            {allCommitted ? "✅ Full commitment confirmed — trade with discipline" : "You are not ready to trade with real money"}
          </div>
          <div style={{height:6,background:"#f1f5f9",borderRadius:3,overflow:"hidden",marginTop:6}}>
            <div style={{height:"100%",width:`${committedCount/totalRules*100}%`,background:allCommitted?C.green:C.indigo,transition:"width 0.4s"}}/>
          </div>
        </div>
      </div>

      <div style={{background:C.ab,border:`2px solid ${C.amber}66`,borderRadius:10,padding:"14px 16px"}}>
        <div style={{fontSize:11,color:C.at,letterSpacing:"0.06em",textTransform:"uppercase",fontWeight:700,marginBottom:8}}>⭐ Today's focus rules</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {focus.map(f => (
            <div key={f.id} style={{display:"flex",gap:8,alignItems:"flex-start",fontSize:12,color:"#92400e"}}>
              <span style={{fontWeight:700}}>•</span>
              <div>
                <div style={{fontWeight:600}}>{f.text}</div>
                <div style={{fontSize:10,color:C.amber,marginTop:2}}>From: {f.sectionTitle}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {brokenCount > 0 && (
        <div style={{background:brokenWarn?C.red:C.rb,border:`1px solid ${brokenWarn?C.red:"#fca5a5"}`,borderRadius:10,padding:"12px 16px",color:brokenWarn?"#fff":C.rt}}>
          <div style={{fontSize:13,fontWeight:800,marginBottom:brokenWarn?6:0}}>
            🚨 Rules broken today: {brokenCount}
          </div>
          {brokenWarn && (
            <div style={{fontSize:12,fontWeight:700}}>⚠ Stop trading — too many rule violations today</div>
          )}
        </div>
      )}

      {GR_SECTIONS.map((section, si) => {
        const ids = section.rules.map((_, ri) => ruleId(si, ri));
        const sectionDone = ids.every(id => committed[id]);
        return (
          <div key={si} style={{background:"#fff",border:`0.5px solid ${sectionDone?"#86efac":"#e5e7eb"}`,borderRadius:10,overflow:"hidden",transition:"border-color 0.3s"}}>
            <div style={{padding:"10px 16px",borderBottom:"0.5px solid #f3f4f6",display:"flex",alignItems:"center",gap:8,background:section.color+"0d"}}>
              <div style={{width:8,height:8,borderRadius:2,background:section.color}}/>
              <span style={{fontSize:13,fontWeight:700,color:"#111"}}>{section.title}</span>
              {sectionDone && <Chip label="✓ Committed" color={C.green}/>}
            </div>
            {section.rules.map((rule, ri) => {
              const id = ruleId(si, ri);
              const isFocus = focusIds.has(id);
              const isCommitted = !!committed[id];
              const ruleNote = notes[id];
              const isEditing = editingNote === id;
              return (
                <div key={ri} style={{
                  borderTop:"0.5px solid #f9fafb",
                  background: isFocus ? "#fef3c7" : "#fff",
                  padding:"10px 16px",
                }}>
                  <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                    <input
                      type="checkbox"
                      checked={isCommitted}
                      onChange={()=>toggleCommit(id)}
                      data-testid={`gr-commit-${id}`}
                      style={{ marginTop:2, accentColor: section.color, cursor:"pointer" }}
                      title="I have read and commit to this rule"
                    />
                    <div style={{flex:1}}>
                      <span style={{
                        fontSize:12,
                        color:"#374151",
                        fontWeight: isFocus ? 600 : 400,
                      }}>
                        <span style={{color:section.color,fontWeight:700,marginRight:4}}>{ri+1}.</span>{rule}
                      </span>
                      <div style={{display:"flex",gap:10,alignItems:"center",marginTop:4,flexWrap:"wrap"}}>
                        <button
                          onClick={()=>recordBroken(id, rule)}
                          style={{padding:"3px 9px",borderRadius:5,background:"#fff",color:C.red,border:`1px solid ${C.red}66`,fontSize:10,cursor:"pointer",fontWeight:600}}
                          data-testid={`gr-broke-${id}`}
                        >
                          I broke this rule today
                        </button>
                        <button
                          onClick={()=>setEditingNote(isEditing ? null : id)}
                          style={{padding:"3px 9px",borderRadius:5,background:"transparent",color:C.indigo,border:"none",fontSize:10,cursor:"pointer",fontWeight:600}}
                          data-testid={`gr-note-${id}`}
                        >
                          {isEditing ? "× Close note" : ruleNote ? "📝 Edit note" : "+ Add note"}
                        </button>
                      </div>
                      {isEditing && (
                        <textarea
                          autoFocus
                          value={ruleNote || ""}
                          onChange={e=>setNote(id, e.target.value)}
                          onBlur={()=>setEditingNote(null)}
                          placeholder="Personal reminder — e.g. 'Remember NVDA March 2024'"
                          style={{...inputStyle, marginTop:6, minHeight:50, resize:"vertical"}}
                        />
                      )}
                      {!isEditing && ruleNote && (
                        <div style={{marginTop:5,padding:"6px 9px",background:"#f8fafc",borderLeft:`3px solid ${section.color}`,borderRadius:4,fontSize:11,color:C.gray,fontStyle:"italic"}}>
                          {ruleNote}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default function RiskToolsTab() {
  const [tool,    setTool]   = useState("sizer");
  // Shared account-size state — blank by default, persisted to
  // localStorage `dt_account_size`, read by Options planner + Analyser.
  const { accountRaw, setAccount: setAccountRaw, account } = useAccountSize();
  // State holds the raw input STRING (not a number) so the user can type
  // intermediate values like "1." without React's controlled-input loop
  // round-tripping through a number-coercion that strips the trailing dot.
  // The `+` conversion happens only inside the math expressions below.
  const [riskPct, setRiskPct]= useState("1");
  const [entry,   setEntry]  = useState("100");
  const [stop,    setStop]   = useState("98");
  const [target,  setTarget] = useState("104");
  const [winR,    setWinR]   = useState("55");
  const [rrR,     setRrR]    = useState("2");
  const [time,    setTime]   = useState(new Date());

  useEffect(() => {
    const t = setInterval(()=>setTime(new Date()), 1000);
    return ()=>clearInterval(t);
  }, []);

  // Math operates on parsed numbers — `+x || 0` handles both numeric AND
  // string state plus empty/invalid inputs.
  const _riskPct = +riskPct || 0;
  const _entry   = +entry   || 0;
  const _stop    = +stop    || 0;
  const _target  = +target  || 0;
  const _winR    = +winR    || 0;
  const _rrR     = +rrR     || 0;

  const riskDol  = account * _riskPct / 100;
  const stopDist = Math.abs(_entry - _stop);
  const shares   = account > 0 && stopDist > 0 ? Math.floor(riskDol / stopDist) : 0;
  const gain     = Math.abs(_target - _entry);
  const rr       = stopDist > 0 ? (gain / stopDist).toFixed(2) : "—";
  const posSize  = shares * _entry;
  const posRisk  = shares * stopDist;
  const potGain  = shares * gain;

  const w = _winR/100, r = _rrR;
  const ruin = Math.pow((1-w*r)/(1+w*r), 20);
  const ruinPct = Math.max(0, Math.min(100, ruin*100)).toFixed(1);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      <div style={{display:"flex", gap:4, flexWrap:"wrap"}}>
        {[{k:"sizer",l:"Position Sizer"},{k:"ruin",l:"Risk of Ruin"},{k:"timer",l:"Session Timer"},{k:"rules",l:"Golden Rules"},{k:"settings",l:"Risk Settings"}].map(v=>(
          <button key={v.k} onClick={()=>setTool(v.k)}
            style={{
              fontSize:12, fontWeight:500, padding:"6px 14px", borderRadius:6, border:"none", cursor:"pointer",
              background: tool===v.k ? "#f1f5f9" : "transparent",
              color:      tool===v.k ? "#0f172a" : "#64748b",
            }}>
            {v.l}
          </button>
        ))}
      </div>

      {tool==="sizer" && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,padding:"16px 20px",boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:10, fontWeight:500, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12}}>Position size calculator</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10}}>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Account size ($)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={accountRaw}
                  onChange={e => setAccountRaw(e.target.value)}
                  placeholder="Enter your account size"
                  data-testid="sizer-account-input"
                  style={{padding:"8px 10px",borderRadius:7,border:`0.5px solid ${account > 0 ? "#d1d5db" : C.amber}`,fontSize:13,fontWeight:600,background:"#fff"}}
                />
              </div>
              {[
                ["Risk per trade (%)","riskPct",riskPct,setRiskPct],
                ["Entry price ($)","entry",entry,setEntry],
                ["Stop price ($)","stop",stop,setStop],
                ["Target price ($)","target",target,setTarget],
              ].map(([label,key,val,setter])=>(
                <div key={key} style={{display:"flex",flexDirection:"column",gap:4}}>
                  <label style={{fontSize:11,color:C.gray,fontWeight:500}}>{label}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={val}
                    onChange={e => setter(e.target.value)}
                    data-testid={`sizer-${key}-input`}
                    style={{padding:"8px 10px",borderRadius:7,border:"0.5px solid #d1d5db",fontSize:13,fontWeight:600,background:"#fff"}}
                  />
                </div>
              ))}
            </div>
            {!account && (
              <div data-testid="sizer-account-warn" style={{marginTop:10,fontSize:11,color:C.amber,fontWeight:600}}>
                ⚠ Enter your account size to calculate position sizing
              </div>
            )}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:8}}>
            {[
              {l:"Shares to buy",   v:shares,             c:"#4f46e5", big:true},
              {l:"Position size",   v:`$${posSize.toFixed(0)}`,    c:"#0f172a"},
              {l:"Max $ risk",      v:`$${posRisk.toFixed(0)}`,    c:"#dc2626"},
              {l:"Potential gain",  v:`$${potGain.toFixed(0)}`,    c:"#16a34a"},
              {l:"Risk:Reward",     v:`${rr}:1`,                   c:parseFloat(rr)>=2?"#16a34a":"#d97706"},
              {l:"Stop distance",   v:`$${stopDist.toFixed(2)}`,   c:"#64748b"},
              {l:"% of account",    v: account > 0 ? `${(posSize/account*100).toFixed(1)}%` : "—", c:"#64748b"},
              {l:"Risk % confirmed",v: account > 0 ? `${(posRisk/account*100).toFixed(2)}%` : "—", c:"#d97706"},
            ].map(m=>(
              <div key={m.l} style={{ background:m.big?"#0f172a":"#fff", border:`1px solid ${m.big?"transparent":"#e2e8f0"}`, borderRadius:8, padding:"12px 14px", boxShadow:m.big?"none":"0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{fontSize:10,color:m.big?"#94a3b8":"#94a3b8",marginBottom:3,textTransform:"uppercase",letterSpacing:"0.06em",fontWeight:500}}>{m.l}</div>
                <div style={{fontSize:m.big?26:18,fontWeight:700,color:m.big?"#fff":m.c, fontFamily:"monospace"}}>{m.v}</div>
              </div>
            ))}
          </div>

          <div style={{background:"#fff",border:"0.5px solid #e5e7eb",borderRadius:10,padding:"14px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
              <span style={{fontSize:12,fontWeight:600,color:"#111"}}>Risk vs target</span>
              <span style={{fontSize:12,color:parseFloat(rr)>=2?C.green:C.amber}}>R:R = {rr}:1 {parseFloat(rr)>=2?"✓ Good":"⚠ Below 2:1"}</span>
            </div>
            <div style={{display:"flex",gap:4,height:20,borderRadius:6,overflow:"hidden"}}>
              <div style={{flex:stopDist,background:C.red,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:9,color:"#fff",fontWeight:600}}>RISK</span>
              </div>
              <div style={{flex:gain,background:C.green,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:9,color:"#fff",fontWeight:600}}>REWARD</span>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.gray,marginTop:4}}>
              <span>Stop: ${stop}</span>
              <span>Entry: ${entry}</span>
              <span>Target: ${target}</span>
            </div>
          </div>
        </div>
      )}

      {tool==="ruin" && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:"#fff",border:"0.5px solid #e5e7eb",borderRadius:10,padding:"16px"}}>
            <p style={{fontSize:13,fontWeight:700,color:"#111",marginBottom:4}}>Risk of ruin calculator</p>
            <p style={{fontSize:11,color:C.gray,marginBottom:14}}>Probability of losing 20 trades in a row and blowing your account, based on your win rate and average R:R.</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Win rate: {winR}%</label>
                </div>
                <input type="range" min="30" max="80" value={winR} onChange={e=>setWinR(+e.target.value)} style={{width:"100%"}}/>
              </div>
              <div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Avg R:R: {rrR}:1</label>
                </div>
                <input type="range" min="1" max="5" step="0.1" value={rrR} onChange={e=>setRrR(+e.target.value)} style={{width:"100%"}}/>
              </div>
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:12,alignItems:"center"}}>
            <div style={{background:parseFloat(ruinPct)<5?C.gb:parseFloat(ruinPct)<20?C.ab:C.rb,border:`2px solid ${parseFloat(ruinPct)<5?C.green:parseFloat(ruinPct)<20?C.amber:C.red}`,borderRadius:14,padding:"24px",textAlign:"center",minWidth:120}}>
              <div style={{fontSize:36,fontWeight:900,color:parseFloat(ruinPct)<5?C.green:parseFloat(ruinPct)<20?C.amber:C.red}}>{ruinPct}%</div>
              <div style={{fontSize:12,color:C.gray,marginTop:4}}>Ruin probability</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[
                {wr:55,rr:2,label:"Good trader",c:C.green},
                {wr:50,rr:1.5,label:"Average trader",c:C.amber},
                {wr:40,rr:1,label:"Struggling trader",c:C.red},
              ].map(p=>{
                const rr = Math.max(0,Math.min(100,Math.pow((1-p.wr/100*p.rr)/(1+p.wr/100*p.rr),20)*100));
                return(
                  <div key={p.label} style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
                    <span style={{color:C.gray,width:130,flexShrink:0}}>{p.label} ({p.wr}% WR, {p.rr}:1)</span>
                    <div style={{flex:1,height:6,background:"#f1f5f9",borderRadius:3}}>
                      <div style={{height:"100%",width:`${rr}%`,background:p.c,borderRadius:3}}/>
                    </div>
                    <span style={{color:p.c,fontWeight:700,width:50,textAlign:"right"}}>{rr.toFixed(1)}%</span>
                  </div>
                );
              })}
              <p style={{fontSize:11,color:C.muted,marginTop:4}}>Target: &lt;5% ruin probability. Achieve with win rate 55%+ and avg R:R 2:1+</p>
            </div>
          </div>
        </div>
      )}

      {tool==="timer" && <SessionTimerPanel time={time}/>}

      {tool==="rules" && <GoldenRulesPanel/>}

      {tool==="settings" && <RiskSettingsPanel/>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
//  RISK SETTINGS PANEL — Phase 2 risk engine UI. Status
//  dashboard (daily P&L, limit remaining, open positions,
//  kill switch) + editable settings synced to /api/risk/*.
// ──────────────────────────────────────────────────────────
const RISK_DEFAULTS = {
  account_size: 10000,
  max_risk_per_trade_pct: 2.0,
  max_daily_loss_pct: 5.0,
  max_open_positions: 5,
  max_per_ticker_pct: 20.0,
  min_confidence: 65,
  max_spread_pct: 15.0,
  kill_switch_active: false,
};

function RiskSettingsPanel() {
  const [form,    setForm]    = useState(RISK_DEFAULTS);
  const [status,  setStatus]  = useState(null);   // /api/risk/daily-loss summary
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const s = await apiJSON("/api/risk/daily-loss");
      setStatus(s);
    } catch (e) {
      setError(e.message || "Failed to load risk status");
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const settings = await apiJSON("/api/risk/settings");
      setForm({ ...RISK_DEFAULTS, ...(settings || {}) });
      await loadStatus();
    } catch (e) {
      setError(e.message || "Failed to load risk settings");
    } finally {
      setLoading(false);
    }
  }, [loadStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        account_size:           +form.account_size           || 0,
        max_risk_per_trade_pct: +form.max_risk_per_trade_pct  || 0,
        max_daily_loss_pct:     +form.max_daily_loss_pct      || 0,
        max_open_positions:     Math.round(+form.max_open_positions || 0),
        max_per_ticker_pct:     +form.max_per_ticker_pct      || 0,
        min_confidence:         Math.round(+form.min_confidence     || 0),
        max_spread_pct:         +form.max_spread_pct          || 0,
        kill_switch_active:     !!form.kill_switch_active,
      };
      const saved = await apiJSON("/api/risk/settings", {
        method: "POST", body: JSON.stringify(payload),
      });
      setForm({ ...RISK_DEFAULTS, ...(saved || {}) });
      setSavedAt(Date.now());
      await loadStatus();
    } catch (e) {
      setError(e.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleKillSwitch = async () => {
    setError("");
    const next = !form.kill_switch_active;
    try {
      const saved = await apiJSON("/api/risk/kill-switch", {
        method: "POST", body: JSON.stringify({ active: next }),
      });
      setForm(f => ({ ...f, ...(saved || {}), kill_switch_active: next }));
      await loadStatus();
    } catch (e) {
      setError(e.message || "Failed to toggle kill switch");
    }
  };

  const killActive = !!form.kill_switch_active;
  const pnl        = status?.daily_pnl ?? 0;
  const pnlUp      = pnl >= 0;
  const remaining  = status?.limit_remaining ?? 0;
  const openPos    = status?.open_positions ?? 0;
  const maxOpen    = status?.max_open_positions ?? (+form.max_open_positions || 0);
  const breached   = !!status?.limit_breached;

  const cards = [
    {
      label: "Daily P&L",
      value: `${pnlUp ? "+" : "−"}$${Math.abs(pnl).toFixed(0)}`,
      color: pnlUp ? C.green : C.red,
    },
    {
      label: "Daily limit remaining",
      value: `$${remaining.toFixed(0)}`,
      color: breached ? C.red : remaining < (status?.max_daily_loss || 0) * 0.25 ? C.amber : C.green,
    },
    {
      label: "Open positions",
      value: `${openPos}/${maxOpen}`,
      color: maxOpen > 0 && openPos >= maxOpen ? C.red : "#0f172a",
    },
    {
      label: "Kill switch",
      value: killActive ? "ACTIVE" : "Off",
      color: killActive ? C.red : C.green,
    },
  ];

  return (
    <div data-testid="risk-settings-panel" style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* Status dashboard — 4 stat cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:8}}>
        {cards.map(c => (
          <div key={c.label} data-testid={`risk-stat-${c.label}`} style={{
            background:"#fff", border:`1px solid ${c.color}33`, borderRadius:8,
            padding:"12px 14px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)",
          }}>
            <div style={{fontSize:10,color:"#94a3b8",marginBottom:3,textTransform:"uppercase",letterSpacing:"0.06em",fontWeight:500}}>{c.label}</div>
            <div style={{fontSize:22,fontWeight:800,color:c.color,fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace"}}>{loading ? "—" : c.value}</div>
          </div>
        ))}
      </div>

      {breached && (
        <div style={{background:C.rb,border:`1px solid ${C.red}`,borderRadius:8,padding:"10px 14px"}}>
          <p style={{fontSize:12,color:C.rt,fontWeight:700}}>🚨 Daily loss limit reached — the risk engine is blocking all new trades today.</p>
        </div>
      )}

      {error && (
        <div data-testid="risk-settings-error" style={{background:C.rb,border:`1px solid #fca5a5`,borderRadius:8,padding:"10px 14px"}}>
          <p style={{fontSize:12,color:C.rt,fontWeight:600}}>⚠ {error}</p>
        </div>
      )}

      {/* Settings form */}
      <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,padding:"16px 20px",boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>
        <div style={{fontSize:10,fontWeight:500,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14}}>Risk settings</div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:16}}>

          {/* Account size */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Account size ($)</label>
            <input
              type="number" inputMode="decimal" autoComplete="off"
              value={form.account_size}
              onChange={e => set("account_size", e.target.value)}
              data-testid="risk-account-size"
              style={inputStyle}
            />
          </div>

          {/* Max open positions */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Max open positions</label>
            <input
              type="number" inputMode="numeric" autoComplete="off" min="0" step="1"
              value={form.max_open_positions}
              onChange={e => set("max_open_positions", e.target.value)}
              data-testid="risk-max-open-positions"
              style={inputStyle}
            />
          </div>

          {/* Min AI confidence */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Min AI confidence (%)</label>
            <input
              type="number" inputMode="numeric" autoComplete="off" min="0" max="100" step="1"
              value={form.min_confidence}
              onChange={e => set("min_confidence", e.target.value)}
              data-testid="risk-min-confidence"
              style={inputStyle}
            />
          </div>

          {/* Max risk per trade % slider */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Max risk per trade</label>
              <span style={{fontSize:11,fontWeight:700,color:C.indigo}}>{(+form.max_risk_per_trade_pct || 0).toFixed(1)}%</span>
            </div>
            <input
              type="range" min="0" max="10" step="0.1"
              value={form.max_risk_per_trade_pct}
              onChange={e => set("max_risk_per_trade_pct", +e.target.value)}
              data-testid="risk-max-risk-per-trade"
              style={{width:"100%",accentColor:C.indigo}}
            />
          </div>

          {/* Max daily loss % slider */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <label style={{fontSize:11,color:C.gray,fontWeight:500}}>Max daily loss</label>
              <span style={{fontSize:11,fontWeight:700,color:C.red}}>{(+form.max_daily_loss_pct || 0).toFixed(1)}%</span>
            </div>
            <input
              type="range" min="0" max="20" step="0.1"
              value={form.max_daily_loss_pct}
              onChange={e => set("max_daily_loss_pct", +e.target.value)}
              data-testid="risk-max-daily-loss"
              style={{width:"100%",accentColor:C.red}}
            />
          </div>
        </div>

        {/* Kill switch + Save */}
        <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginTop:18,paddingTop:16,borderTop:"0.5px solid #f1f5f9"}}>
          <button
            onClick={toggleKillSwitch}
            data-testid="risk-kill-switch-toggle"
            style={{
              padding:"9px 18px", borderRadius:7, border:"none", cursor:"pointer",
              fontSize:13, fontWeight:700, letterSpacing:"0.02em",
              background: killActive ? C.red : "#fff",
              color:      killActive ? "#fff" : C.red,
              boxShadow:  killActive ? "0 2px 8px rgba(220,38,38,0.35)" : "none",
              outline:    killActive ? "none" : `1px solid ${C.red}66`,
            }}>
            {killActive ? "🛑 KILL SWITCH ACTIVE — Click to disable" : "Activate kill switch"}
          </button>

          <div style={{flex:1}}/>

          {savedAt && !saving && (
            <span style={{fontSize:11,color:C.green,fontWeight:600}}>✓ Saved</span>
          )}
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="risk-save-settings"
            style={{
              padding:"9px 22px", borderRadius:7, border:"none",
              cursor: saving || loading ? "default" : "pointer",
              fontSize:13, fontWeight:700, background:C.indigo, color:"#fff",
              opacity: saving || loading ? 0.6 : 1,
            }}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>

        <p style={{fontSize:11,color:C.muted,marginTop:12}}>
          These limits power the risk engine — every AI trade suggestion is checked against them before it can be approved.
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
//  SESSION TIMER PANEL — world clocks, big NYSE countdown,
//  phase-aware timeline bar, next-events panel.
// ──────────────────────────────────────────────────────────
function SessionTimerPanel({ time }) {
  const phase = computeMarketPhase(time);

  // Daily max-loss inputs — blank by default, persisted.
  const [maxLossAcct, setMaxLossAcct] = useState(() => {
    try { return localStorage.getItem("dt_maxloss_account") || ""; } catch { return ""; }
  });
  const [maxLossPct, setMaxLossPct] = useState(() => {
    try { return localStorage.getItem("dt_maxloss_pct") || ""; } catch { return ""; }
  });
  useEffect(() => { try { maxLossAcct === "" ? localStorage.removeItem("dt_maxloss_account") : localStorage.setItem("dt_maxloss_account", String(maxLossAcct)); } catch { /* noop */ } }, [maxLossAcct]);
  useEffect(() => { try { maxLossPct  === "" ? localStorage.removeItem("dt_maxloss_pct")     : localStorage.setItem("dt_maxloss_pct",     String(maxLossPct)); } catch { /* noop */ } }, [maxLossPct]);
  const acctNum = parseFloat(maxLossAcct);
  const pctNum  = parseFloat(maxLossPct);
  const stopDollar = Number.isFinite(acctNum) && Number.isFinite(pctNum) && acctNum > 0 && pctNum > 0
    ? acctNum * pctNum / 100
    : 0;

  const ZONES = [
    { label: "🗽 ET (New York)", tz: "America/New_York" },
    { label: "🇬🇧 UK (London)",   tz: "Europe/London"    },
    { label: "🇦🇪 UAE (Dubai)",   tz: "Asia/Dubai"       },
    { label: "🏠 YOUR LOCAL",      tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
  ];

  // Big-countdown palette per phase
  const phaseStyle = {
    OPEN:       { bg:"linear-gradient(135deg,#065f46,#047857)", accent:"#86efac", title:"🟢 NYSE OPEN SESSION",    sub:"Trade with conviction — prime 90-min window" },
    MID:        { bg:"linear-gradient(135deg,#78350f,#92400e)", accent:"#fcd34d", title:"⚠ MID-SESSION",            sub:"Avoid new entries — chop zone" },
    POWER:      { bg:"linear-gradient(135deg,#064e3b,#065f46)", accent:"#86efac", title:"⚡ POWER HOUR",              sub:"Last hour — momentum plays" },
    PREMARKET:  { bg:"linear-gradient(135deg,#1e3a8a,#1e40af)", accent:"#93c5fd", title:"🌅 PRE-MARKET ACTIVE",      sub:"Scan gappers — no live entries" },
    AFTERHOURS: { bg:"linear-gradient(135deg,#581c87,#6b21a8)", accent:"#d8b4fe", title:"🌙 AFTER HOURS",            sub:"Earnings releases — thin liquidity" },
    OVERNIGHT:  { bg:"linear-gradient(135deg,#0f172a,#1e293b)", accent:"#94a3b8", title:"🌑 OVERNIGHT",               sub:"Market closed — rest your eyes" },
    CLOSED:     { bg:"linear-gradient(135deg,#0f172a,#1e293b)", accent:"#94a3b8", title:"🔴 MARKET CLOSED",           sub:"Review today — plan tomorrow" },
    CLOSED_WEEKEND: { bg:"linear-gradient(135deg,#0f172a,#1e293b)", accent:"#94a3b8", title:"🏖 WEEKEND — MARKET CLOSED", sub:"Catch up on research" },
  }[phase.phase] || { bg:C.navy, accent:"#e2e8f0", title:"", sub:"" };

  // Timeline fractions — each phase spans 4am → 8pm ET (16h = 960 min)
  const TL_START = 4*60, TL_END = 20*60, TL_SPAN = TL_END - TL_START;
  const tlSegments = [
    { label:"Pre-Market",   start:4*60,    end:9*60+30, color:"#1e40af" },
    { label:"Open Session", start:9*60+30, end:11*60,   color:"#10b981" },
    { label:"Mid-Session",  start:11*60,   end:15*60,   color:"#f59e0b" },
    { label:"Power Hour",   start:15*60,   end:16*60,   color:"#10b981" },
    { label:"After Hours",  start:16*60,   end:20*60,   color:"#8b5cf6" },
  ];
  const markerPos = Math.min(1, Math.max(0, (phase.etMins - TL_START) / TL_SPAN));

  // Next-events panel — always show the next 4 interesting moments.
  function nextEvents() {
    const events = [];
    const mins = phase.etMins;
    const day = phase.et.getDay();
    const wd = day >= 1 && day <= 5;
    const pushIf = (targetMins, label, icon) => {
      if (wd && mins < targetMins) {
        const secs = (targetMins - mins) * 60 - phase.et.getSeconds();
        events.push({ label, icon, secs });
      }
    };
    pushIf(9*60+30, "Market Opens",        "🟢");
    pushIf(11*60,   "Mid-Session starts",  "⚠");
    pushIf(15*60,   "Power Hour starts",   "⚡");
    pushIf(16*60,   "Market Closes",       "🔴");
    // Fallback if we're past 4pm or weekend — show the next open
    if (events.length === 0) {
      events.push({ label: phase.nextLabel, icon:"📅", secs: phase.secsUntilNext });
    }
    return events.slice(0, 4);
  }
  const events = nextEvents();
  function fmtSecs(t) {
    const total = Math.max(0, Math.floor(t));
    const h = Math.floor(total/3600);
    const m = Math.floor((total%3600)/60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* World clock grid */}
      <div data-testid="session-timer-world-clocks" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:10 }}>
        {ZONES.map(z => (
          <div key={z.label} style={{ background:"#fff", border:"0.5px solid #e5e7eb", borderRadius:10, padding:"11px 14px" }}>
            <div style={{ fontSize:10, color:C.gray, fontWeight:700, letterSpacing:"0.05em", marginBottom:4 }}>{z.label}</div>
            <div style={{ fontSize:22, fontWeight:800, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", color:"#111" }}>
              {fmtTime(time, z.tz)}
            </div>
            <div style={{ fontSize:10, color:C.gray, marginTop:2 }}>
              {time.toLocaleDateString("en-GB", { timeZone: z.tz, weekday:"short", day:"2-digit", month:"short" })}
            </div>
          </div>
        ))}
      </div>

      {/* Big phase countdown card */}
      <div data-testid="session-timer-big-countdown" style={{
        background: phaseStyle.bg, color:"#fff", borderRadius:14, padding:"22px 24px",
        textAlign:"center", boxShadow:"0 4px 24px rgba(15,23,42,0.12)",
      }}>
        <div style={{ fontSize:11, letterSpacing:"0.12em", color:phaseStyle.accent, fontWeight:700, textTransform:"uppercase" }}>
          {phaseStyle.title}
        </div>
        <div style={{ fontSize:13, color:"#cbd5e1", marginTop:4, marginBottom:14 }}>
          {phaseStyle.sub}
        </div>
        <div style={{ fontSize:11, color:"#94a3b8", letterSpacing:"0.08em", textTransform:"uppercase" }}>
          {phase.nextLabel}
        </div>
        <div style={{ fontSize:54, fontWeight:900, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", color:phaseStyle.accent, marginTop:4, letterSpacing:"-0.02em" }}>
          {phase.hms.hh > 0 && `${phase.hms.hh}h `}{String(phase.hms.mm).padStart(2,"0")}m {String(phase.hms.ss).padStart(2,"0")}s
        </div>
      </div>

      {/* Timeline bar */}
      <div data-testid="session-timer-timeline" style={{ background:"#fff", border:"0.5px solid #e5e7eb", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#111", marginBottom:12 }}>
          Trading day timeline · ET
        </div>
        <div style={{ position:"relative", height:42 }}>
          {/* Segments */}
          <div style={{ display:"flex", width:"100%", height:22, borderRadius:6, overflow:"hidden", border:"1px solid #e5e7eb" }}>
            {tlSegments.map(seg => {
              const w = ((seg.end - seg.start) / TL_SPAN) * 100;
              return (
                <div key={seg.label}
                     style={{ width:`${w}%`, background:seg.color, opacity:0.9, display:"flex",
                              alignItems:"center", justifyContent:"center", fontSize:9,
                              fontWeight:700, color:"#fff", letterSpacing:"0.04em", whiteSpace:"nowrap" }}>
                  {w > 6 ? seg.label : ""}
                </div>
              );
            })}
          </div>
          {/* "YOU ARE HERE" marker */}
          <div style={{
            position:"absolute", top:-2, left:`calc(${markerPos * 100}% - 1px)`,
            width:2, height:26, background:"#ef4444",
          }}/>
          <div style={{
            position:"absolute", top:22, left:`${markerPos * 100}%`, transform:"translateX(-50%)",
            fontSize:10, fontWeight:800, color:"#ef4444", whiteSpace:"nowrap",
          }}>
            ▲ YOU ARE HERE
          </div>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:C.gray, marginTop:16 }}>
          <span>4:00 AM</span>
          <span>9:30 AM</span>
          <span>11:00 AM</span>
          <span>3:00 PM</span>
          <span>4:00 PM</span>
          <span>8:00 PM</span>
        </div>
      </div>

      {/* Next events */}
      <div data-testid="session-timer-next-events" style={{ background:"#fff", border:"0.5px solid #e5e7eb", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#111", marginBottom:10 }}>Up next</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:8 }}>
          {events.map((e, i) => (
            <div key={i} style={{ background:"#f8fafc", border:"0.5px solid #e5e7eb", borderRadius:8, padding:"10px 12px" }}>
              <div style={{ fontSize:11, fontWeight:600, color:C.gray }}>{e.icon} {e.label}</div>
              <div style={{ fontSize:16, fontWeight:800, fontFamily:"ui-monospace, Menlo, monospace", color:"#111", marginTop:2 }}>
                in {fmtSecs(e.secs)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Daily max loss tracker — user-configured, persisted to LS */}
      <div data-testid="session-timer-maxloss" style={{background:"#fff",border:"0.5px solid #e5e7eb",borderRadius:10,padding:"14px 16px"}}>
        <p style={{fontSize:12,fontWeight:700,color:"#111",marginBottom:10}}>Daily max loss tracker</p>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:10,color:C.gray}}>Account ($)</label>
            <input
              type="number"
              inputMode="decimal"
              value={maxLossAcct}
              onChange={e => setMaxLossAcct(e.target.value)}
              placeholder="Enter amount"
              data-testid="maxloss-account-input"
              style={{padding:"7px 9px",borderRadius:6,border:`0.5px solid ${acctNum > 0 ? "#d1d5db" : "#fbbf24"}`,fontSize:12,width:130}}
            />
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:10,color:C.gray}}>Max loss %</label>
            <input
              type="number"
              inputMode="decimal"
              value={maxLossPct}
              onChange={e => setMaxLossPct(e.target.value)}
              placeholder="Enter %"
              data-testid="maxloss-pct-input"
              style={{padding:"7px 9px",borderRadius:6,border:`0.5px solid ${pctNum > 0 ? "#d1d5db" : "#fbbf24"}`,fontSize:12,width:110}}
            />
          </div>
        </div>
        {stopDollar > 0 ? (
          <div style={{marginTop:12,background:C.rb,borderRadius:8,padding:"10px 14px"}}>
            <p style={{fontSize:12,color:C.rt,fontWeight:600}}>
              ⚠ If you lose more than {pctNum}% (${stopDollar.toFixed(0)} on ${acctNum.toLocaleString()}) in a single day — STOP TRADING immediately.
            </p>
            <p style={{fontSize:11,color:C.rt,marginTop:4}}>Revenge trading after a bad day is the #1 account killer. Walk away, review tomorrow.</p>
          </div>
        ) : (
          <div style={{marginTop:12,background:C.ab,borderRadius:8,padding:"10px 14px"}}>
            <p style={{fontSize:12,color:C.at,fontWeight:600}}>⚠ Enter your account size and daily max loss % above to activate the tracker.</p>
          </div>
        )}
      </div>
    </div>
  );
}
